import { useEffect, useState, useRef, useCallback } from 'react'
import { round } from '@10000/common'
import { useForceUpdate } from '@10000/react/src/hooks/useForceUpdate.js'
import { Avatar, Box, MeterPie } from './'

const isMouseUp = event => event.type === 'mouseup' || event.type === 'pointerup'

export const Meter = ({ value, onChange, integer, lineHeight = 14, answers = [], avatarSize = 'smallest', disabled }) => {
  const meterRef = useRef()
  const update = useForceUpdate()

  const valueToAngle = useCallback((value) => {
    if(typeof(value) !== 'number') {
      return undefined
    } else if(integer) {
      return value * 30 + 15
    } else if(value < 0.5) {
      return value * 60
    } else if(value > 4.5) {
      return 150 + (value - 4.5) * 60
    } else {
      return (value + 0.5) * 30
    }
  }, [integer])
  const [angle, setAngle] = useState(valueToAngle(value))

  useEffect(() => {
    setAngle(valueToAngle(value))
  }, [value, valueToAngle])

  const shownAnswers = answers.filter((answer, index) => (answer.timestamp > (Date.now() - 5000)) || (index === 0))

  useEffect(() => {
    if(shownAnswers.length > 1){
      const nextAnswerTimeout = Math.min(...shownAnswers.map(answer =>
        answer.timestamp - (Date.now() - 5000)
      ))
      const handle = setTimeout(update, nextAnswerTimeout)
      return () => clearTimeout(handle)
    }
    }, [shownAnswers, update])

  const calculateAngle = (event) => {
    if ((!isMouseUp(event) && event.buttons === 0) || disabled) return

    const rect = meterRef.current.getBoundingClientRect()
    const x = (event.clientX - rect.left) - (rect.width / 2)
    const y = (event.clientY - rect.top) - rect.height

    //'x, y' is relative to the centre bottom of the meter, if 'y' <= 0 (below the meter) set to min (0) or max (180) and depending on the side of 'x'
    const newAngle = y < 0
      ? (Math.atan(y / x) * (180 / Math.PI) + 180) % 180
      : x > 0 ? 180 : 0

    const rawScore = (newAngle * 6) / 180
    const roundedScore = integer ? Math.min(Math.floor(rawScore), 5) : newAngle
    const adjustedScore = newAngle < 30 ? (rawScore / 2)
      : newAngle > 150 ? (4.5 + (rawScore - 5) / 2)
      : (rawScore - 0.5)

    setAngle((integer && isMouseUp(event))
      ? valueToAngle(roundedScore)
      : newAngle
    )

    onChange?.(integer ? roundedScore : round(adjustedScore, 1), isMouseUp(event))
  }

  return (
    <Box
      ref={meterRef}
      fullWidth
      backgroundSize="cover"
      aspect="2/1"
      justifyContent="center"
      position="relative"
      onMouseDown={calculateAngle}
      onMouseMove={calculateAngle}
      onMouseUp={calculateAngle}
      onPointerDown={calculateAngle}
      onPointerMove={calculateAngle}
      onPointerUp={calculateAngle}
      cursor="pointer"
      style={{ touchAction: 'none' }}
    >
      <MeterPie />

      {angle !== undefined && <Box
        width="37.5%"
        mr="37.5%"
        background="black"
        position="absolute"
        height={lineHeight}
        bottom={-lineHeight / 2}
        radius={24}
        rotate={angle}
        transformOrigin="center right"
        transition="transform 0.1s"
        border="1px solid var(--darkgrey)"
      />}
      {shownAnswers.map(({ answer, avatar }, index) =>
        <Box
          key={`${index}${answer}`}
          position="absolute"
          bottom={0}
          alignItems="center"
          width="55%"
          mr="55%"
          zIndex={answers.length - index}
          rotate={valueToAngle(answer)}
          transformOrigin="center right"
        >
          <Box rotate={-valueToAngle(answer)} position="absolute">
            <Avatar size={avatarSize} index={avatar} />
          </Box>
        </Box>
      )}
    </Box>
  )
}